import { useState, useEffect, useRef, useCallback } from "react"
import { ChevronDown, Trash2, Loader2, AlertTriangle } from "lucide-react"
import { getCashLedger, deleteCashEntry } from "@/lib/api/hive"
import type { CashLedgerEntry } from "@/types/portfolio"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

const PAGE_SIZE = 20

const TYPE_LABELS: Record<CashLedgerEntry["type"], string> = {
	DEPOSIT: "Deposit",
	WITHDRAWAL: "Withdrawal",
	BUY_DEBIT: "Buy",
	SELL_CREDIT: "Sell",
	DIVIDEND: "Dividend",
}

const isPositive = (type: CashLedgerEntry["type"]): boolean =>
	type === "DEPOSIT" || type === "SELL_CREDIT" || type === "DIVIDEND"

const isDeletable = (type: CashLedgerEntry["type"]): boolean => type === "DEPOSIT" || type === "WITHDRAWAL"

interface CashLedgerPanelProps {
	portfolioId: string
	currency: string
	cashBalance?: number
	onEntryDeleted?: () => void
	refreshKey?: number
}

export function CashLedgerPanel({
	portfolioId,
	currency,
	cashBalance: balanceProp,
	onEntryDeleted,
	refreshKey,
}: CashLedgerPanelProps) {
	const [open, setOpen] = useState(false)
	const [entries, setEntries] = useState<CashLedgerEntry[]>([])
	const [total, setTotal] = useState(0)
	const [offset, setOffset] = useState(0)
	const [isLoading, setIsLoading] = useState(false)
	const [isLoadingMore, setIsLoadingMore] = useState(false)
	const [loadError, setLoadError] = useState<string | null>(null)
	const [deletingId, setDeletingId] = useState<string | null>(null)
	const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
	const prevRefreshKey = useRef(refreshKey)

	const loadEntries = useCallback(
		async (reset = false) => {
			const currentOffset = reset ? 0 : offset
			if (reset) {
				setIsLoading(true)
			} else {
				setIsLoadingMore(true)
			}
			try {
				const result = await getCashLedger(portfolioId, PAGE_SIZE, currentOffset)
				if (reset) {
					setEntries(result.entries)
					setOffset(PAGE_SIZE)
				} else {
					setEntries((prev) => [...prev, ...result.entries])
					setOffset(currentOffset + PAGE_SIZE)
				}
				setTotal(result.total)
				setLoadError(null)
			} catch (err) {
				console.error("Failed to load cash ledger:", err)
				setLoadError(err instanceof Error ? err.message : "Failed to load cash activity")
			} finally {
				setIsLoading(false)
				setIsLoadingMore(false)
			}
		},
		[portfolioId, offset],
	)

	// Load when panel is opened
	useEffect(() => {
		if (open && entries.length === 0) {
			loadEntries(true)
		}
	}, [open]) // eslint-disable-line react-hooks/exhaustive-deps

	// Reload entries when parent signals a mutation (deposit/withdrawal)
	useEffect(() => {
		if (refreshKey === prevRefreshKey.current) return
		prevRefreshKey.current = refreshKey
		if (open) {
			loadEntries(true)
		} else {
			setEntries([])
			setOffset(0)
		}
	}, [refreshKey, open]) // eslint-disable-line react-hooks/exhaustive-deps

	const handleDelete = async (entryId: string) => {
		setDeletingId(entryId)
		try {
			await deleteCashEntry(portfolioId, entryId)
			setEntries((prev) => prev.filter((e) => e.id !== entryId))
			setTotal((prev) => prev - 1)
			setConfirmDeleteId(null)
			onEntryDeleted?.()
		} catch (err) {
			console.error("Failed to delete cash entry:", err)
		} finally {
			setDeletingId(null)
		}
	}

	// Compute running balance from newest-to-oldest using base_amount (converted
	// to portfolio base currency) so mixed-currency entries are summed correctly.
	// If we have the total cash balance from the API, use it as the anchor for
	// the most recent entry so pagination doesn't reset the column to 0.
	const runningBalances: number[] = new Array(entries.length)
	let running = balanceProp ?? 0
	for (let i = 0; i < entries.length; i++) {
		runningBalances[i] = running
		const amount = entries[i].base_amount ?? entries[i].amount
		// Subtract this entry's effect to get the balance before this entry
		if (isPositive(entries[i].type)) {
			running -= amount
		} else {
			running += amount
		}
	}
	const balanceByIndex = runningBalances

	return (
		<div className="bg-white/60 dark:bg-black/20 backdrop-blur-md border border-sky-900/10 dark:border-sky-100/10 rounded-xl">
			{/* Header / Toggle */}
			<button
				type="button"
				className="w-full flex items-center justify-between px-4 py-3 text-left"
				onClick={() => setOpen((o) => !o)}
			>
				<span className="font-semibold text-sky-950 dark:text-sky-50 text-sm">Cash Activity</span>
				<ChevronDown
					className={cn("h-4 w-4 text-muted-foreground transition-transform", open && "rotate-180")}
				/>
			</button>

			{open && (
				<div className="border-t border-sky-900/10 dark:border-sky-100/10">
					{isLoading ? (
						<div className="flex items-center justify-center py-8">
							<Loader2 className="h-5 w-5 animate-spin text-sky-500" />
						</div>
					) : loadError ? (
						<div className="flex items-center gap-2 py-6 px-4 text-sm text-amber-600 dark:text-amber-400">
							<AlertTriangle className="h-4 w-4 flex-shrink-0" />
							<span>Cash activity unavailable — a holding uses a currency we can't convert yet.</span>
						</div>
					) : entries.length === 0 ? (
						<div className="py-8 text-center text-muted-foreground text-sm">No cash activity yet</div>
					) : (
						<div className="overflow-x-auto">
							<table className="w-full text-sm">
								<thead>
									<tr className="border-b border-sky-900/10 dark:border-sky-100/10 text-muted-foreground text-xs uppercase tracking-wider">
										<th className="text-left px-4 py-2 font-medium">Date</th>
										<th className="text-left px-4 py-2 font-medium">Type</th>
										<th className="text-left px-4 py-2 font-medium hidden sm:table-cell">
											Description
										</th>
										<th className="text-right px-4 py-2 font-medium">Amount</th>
										<th className="text-right px-4 py-2 font-medium">Balance</th>
										<th className="px-2 py-2" />
									</tr>
								</thead>
								<tbody>
									{entries.map((entry, idx) => (
										<tr
											key={entry.id}
											className="border-b border-sky-900/5 dark:border-sky-100/5 last:border-0 hover:bg-sky-50/30 dark:hover:bg-sky-900/10"
										>
											<td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">
												{new Date(entry.date).toLocaleDateString(undefined, {
													year: "numeric",
													month: "short",
													day: "numeric",
												})}
											</td>
											<td className="px-4 py-2.5 whitespace-nowrap">
												<span
													className={cn(
														"text-xs font-medium px-1.5 py-0.5 rounded",
														isPositive(entry.type)
															? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
															: "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400",
													)}
												>
													{TYPE_LABELS[entry.type]}
												</span>
											</td>
											<td className="px-4 py-2.5 text-muted-foreground text-xs hidden sm:table-cell">
												{entry.description || <span className="opacity-40">—</span>}
											</td>
											<td
												className={cn(
													"px-4 py-2.5 text-right font-medium whitespace-nowrap",
													isPositive(entry.type)
														? "text-green-600 dark:text-green-400"
														: "text-red-600 dark:text-red-400",
												)}
											>
												{isPositive(entry.type) ? "+" : "-"}
												{entry.currency}{" "}
												{entry.amount.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</td>
											<td className="px-4 py-2.5 text-right text-muted-foreground whitespace-nowrap">
												{currency}{" "}
												{balanceByIndex[idx].toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</td>
											<td className="px-2 py-2.5 text-right">
												{isDeletable(entry.type) &&
													(confirmDeleteId === entry.id ? (
														<div className="flex items-center gap-1 justify-end">
															<Button
																size="sm"
																variant="outline"
																className="h-6 px-2 text-xs text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
																onClick={() => handleDelete(entry.id)}
																disabled={deletingId === entry.id}
															>
																{deletingId === entry.id ? (
																	<Loader2 className="h-3 w-3 animate-spin" />
																) : (
																	"Yes"
																)}
															</Button>
															<Button
																size="sm"
																variant="ghost"
																className="h-6 px-2 text-xs"
																onClick={() => setConfirmDeleteId(null)}
																disabled={deletingId === entry.id}
															>
																No
															</Button>
														</div>
													) : (
														<button
															type="button"
															className="p-1 rounded text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
															onClick={() => setConfirmDeleteId(entry.id)}
															title="Delete entry"
														>
															<Trash2 className="h-3.5 w-3.5" />
														</button>
													))}
											</td>
										</tr>
									))}
								</tbody>
							</table>

							{entries.length < total && (
								<div className="flex justify-center py-3 border-t border-sky-900/10 dark:border-sky-100/10">
									<Button
										variant="ghost"
										size="sm"
										onClick={() => loadEntries(false)}
										disabled={isLoadingMore}
										className="text-sky-600 hover:text-sky-700 text-xs"
									>
										{isLoadingMore ? (
											<>
												<Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
												Loading...
											</>
										) : (
											`Load more (${total - entries.length} remaining)`
										)}
									</Button>
								</div>
							)}
						</div>
					)}
				</div>
			)}
		</div>
	)
}
